import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail, Sparkles, User, Users } from "lucide-react";
import { z } from "zod";

import logoLight from "@/assets/logo-light.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
const authSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  username: z.string().min(3, "Username must be at least 3 characters").optional(),
});

const emailSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const resetSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    passwordConfirm: z.string().min(6, "Password must be at least 6 characters"),
  })
  .refine((v) => v.password === v.passwordConfirm, {
    path: ["passwordConfirm"],
    message: "Passwords do not match",
  });

const friendlyAuthError = (message: string, lang: string) => {
  const m = (message ?? "").toLowerCase();

  // Safari and some blockers often surface CORS/origin issues as a generic fetch error
  if (m.includes("load failed") || m.includes("failed to fetch")) {
    return lang === "pt"
      ? "Falha de conexão ao autenticar. Se você estiver usando um domínio personalizado, adicione-o nas URLs permitidas de autenticação e tente novamente."
      : "Connection failed during login. If you're using a custom domain, add it to the allowed auth URLs and try again.";
  }

  return message;
};

const Auth = () => {
  const [mode, setMode] = useState<"login" | "signup" | "forgot" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [username, setUsername] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);

  const { toast } = useToast();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement>(null);
  const recoveryHandledRef = useRef(false);

  const { signIn, signUp, resetPassword, exchangeCodeForSession, updatePassword, user } = useAuth();

  // Get the previous page from state or query param (returnTo) or default to home
  const returnToParam = new URLSearchParams(location.search).get("returnTo");
  const from = returnToParam && returnToParam.startsWith("/") ? returnToParam : (location.state as any)?.from || "/";

  // Check for referral code on mount - use secure RPC function that works without auth
  useEffect(() => {
    const referralCode = sessionStorage.getItem("referralCode");
    if (referralCode) {
      // Fetch referrer's name using secure RPC (works without authentication)
      supabase
        .rpc("get_referrer_by_code", { _code: referralCode })
        .then(({ data }) => {
          if (data && data.length > 0 && data[0].username) {
            setReferrerName(data[0].username);
            setMode("signup"); // Auto-switch to signup mode
          } else {
            // Set generic name if username not found but code valid
            const storedReferrerId = sessionStorage.getItem("referrerId");
            if (storedReferrerId) {
              setReferrerName(locale.language === "pt" ? "Um amigo" : "A friend");
              setMode("signup");
            }
          }
        });
    }
  }, [locale.language]);

  useEffect(() => {
    const titleBase = "Callyout";
    if (mode === "signup") document.title = `${titleBase} | Sign Up`;
    else if (mode === "forgot") document.title = `${titleBase} | Forgot Password`;
    else if (mode === "reset") document.title = `${titleBase} | Reset Password`;
    else document.title = `${titleBase} | Log In`;
  }, [mode]);

  useEffect(() => {
    if (user && mode !== "reset") {
      // Check for pending invite
      const pendingInvite = sessionStorage.getItem("pendingInvite");
      if (pendingInvite) {
        sessionStorage.removeItem("pendingInvite");
        navigate(`/invite/${pendingInvite}`);
      } else {
        navigate(from);
      }
    }
  }, [user, navigate, from, mode]);

  // If user arrives from password recovery email, exchange the code for a session and show reset UI.
  useEffect(() => {
    if (recoveryHandledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const type = params.get("type");

    if (!code) return;
    if (type && type !== "recovery") return;

    recoveryHandledRef.current = true;
    setMode("reset");
    setIsLoading(true);

    exchangeCodeForSession(code)
      .then(({ error }) => {
        if (error) {
          toast({
            title: locale.language === "pt" ? "Erro" : "Error",
            description:
              locale.language === "pt"
                ? "Link de recuperação inválido ou expirado. Solicite um novo email."
                : "Recovery link is invalid or expired. Please request a new email.",
            variant: "destructive",
          });
          setMode("login");
        } else {
          // Remove code/type from URL to avoid re-processing
          navigate("/auth", { replace: true, state: location.state });
        }
      })
      .finally(() => setIsLoading(false));
  }, [exchangeCodeForSession, navigate, toast, locale.language, location.state]);

  // Handle click outside to go back
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (mode === "reset") return;
    if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
      navigate(-1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === "reset") {
        resetSchema.parse({ password, passwordConfirm });

        const { error } = await updatePassword(password);
        if (error) {
          toast({
            title: locale.language === "pt" ? "Erro" : "Error",
            description: friendlyAuthError(error.message, locale.language),
            variant: "destructive",
          });
          return;
        }

        toast({
          title: locale.language === "pt" ? "Senha atualizada!" : "Password updated!",
          description:
            locale.language === "pt"
              ? "Você já pode entrar com sua nova senha."
              : "You can now log in with your new password.",
        });

        setPassword("");
        setPasswordConfirm("");
        setMode("login");
        navigate(from);
        return;
      }

      if (mode === "forgot") {
        emailSchema.parse({ email });
        const { error } = await resetPassword(email);
        if (error) {
          toast({
            title: locale.language === "pt" ? "Erro" : "Error",
            description: friendlyAuthError(error.message, locale.language),
            variant: "destructive",
          });
        } else {
          toast({
            title: locale.language === "pt" ? "Email enviado!" : "Email sent!",
            description:
              locale.language === "pt"
                ? "Verifique sua caixa de entrada para redefinir sua senha"
                : "Check your inbox to reset your password",
          });
          setMode("login");
        }
        return;
      }

      const validationData =
        mode === "login" ? { email, password } : { email, password, username: username || undefined };

      authSchema.parse(validationData);

      if (mode === "login") {
        const { error } = await signIn(email, password);
        if (error) {
          toast({
            title: locale.language === "pt" ? "Erro ao entrar" : "Login failed",
            description: friendlyAuthError(error.message, locale.language),
            variant: "destructive",
          });
        } else {
          navigate(from);
        }
      } else {
        // Get referrer info from session storage
        const referralCode = sessionStorage.getItem("referralCode");
        const referrerId = sessionStorage.getItem("referrerId");
        
        // Get user's location from IP geolocation (fallback to timezone)
        let signupLocation: string | undefined;
        try {
          const { data: locationData } = await supabase.functions.invoke("get-signup-location");
          signupLocation = locationData?.location || Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
        } catch {
          signupLocation = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
        }
        
        const { error } = await signUp(email, password, username, referrerId || undefined, signupLocation);
        if (error) {
          toast({
            title: locale.language === "pt" ? "Erro ao cadastrar" : "Signup failed",
            description: friendlyAuthError(error.message, locale.language),
            variant: "destructive",
          });
          
          // Notify admins of signup failure (fire and forget)
          try {
            supabase.functions.invoke("notify-signup-error", {
              body: {
                email,
                username: username || undefined,
                error_message: error.message,
                error_code: (error as any).code || undefined,
                referral_code: referralCode || undefined,
                user_agent: navigator.userAgent,
                language: locale.language,
              },
            }).catch(() => {
              // Silently fail - don't block user experience
            });
          } catch {
            // Silently fail
          }
        } else {
          // Clear referral data from session
          sessionStorage.removeItem("referralCode");
          sessionStorage.removeItem("referrerId");
          toast({
            title: locale.language === "pt" ? "Conta criada!" : "Account created!",
            description:
              locale.language === "pt"
                ? "Você recebeu 100 CallyCoins para começar!"
                : "You received 100 CallyCoins to start!",
          });
          navigate(from);
        }
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        toast({
          title: locale.language === "pt" ? "Erro de validação" : "Validation error",
          description: err.errors[0].message,
          variant: "destructive",
        });
      } else {
        const message = err instanceof Error ? err.message : String(err);
        toast({
          title: locale.language === "pt" ? "Erro" : "Error",
          description: friendlyAuthError(message, locale.language),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden cursor-pointer"
      onClick={handleBackgroundClick}
    >
      <h1 className="sr-only">Callyout authentication</h1>

      {/* Background effects */}
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-[100px] animate-float pointer-events-none" />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px] animate-float pointer-events-none"
        style={{ animationDelay: "-3s" }}
      />

      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10 cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="glass-card p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={logoLight} alt="Callyout" className="h-10 mx-auto mb-4" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium">
              <Sparkles size={16} />
              {mode === "forgot"
                ? locale.language === "pt"
                  ? "Recuperar senha"
                  : "Reset password"
                : mode === "reset"
                  ? locale.language === "pt"
                    ? "Definir nova senha"
                    : "Set new password"
                  : locale.language === "pt"
                    ? "Comece a prever agora!"
                    : "Start predicting now!"}
            </span>
          </div>

          {/* Referral badge */}
          {referrerName && mode === "signup" && (
            <div className="flex items-center justify-center gap-2 mb-4 px-4 py-2 rounded-full bg-success/10 border border-success/30 text-success text-sm">
              <Users size={16} />
              <span>
                {locale.language === "pt"
                  ? `Convidado por ${referrerName}`
                  : `Invited by ${referrerName}`}
              </span>
            </div>
          )}

          {/* Tabs - only show for login/signup */}
          {(mode === "login" || mode === "signup") && (
            <div className="flex mb-8 bg-muted/50 rounded-lg p-1">
              <button
                onClick={() => setMode("login")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === "login" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                {locale.language === "pt" ? "Entrar" : "Log In"}
              </button>
              <button
                onClick={() => setMode("signup")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === "signup" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                {locale.language === "pt" ? "Cadastrar" : "Sign Up"}
              </button>
            </div>
          )}

          {/* Back button for forgot/reset */}
          {(mode === "forgot" || mode === "reset") && (
            <button
              onClick={() => {
                setMode("login");
                setPassword("");
                setPasswordConfirm("");
              }}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              {locale.language === "pt" ? "Voltar ao login" : "Back to login"}
            </button>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={locale.language === "pt" ? "Nome de usuário" : "Username"}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            )}

            {mode !== "reset" && (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            )}

            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={
                    mode === "reset"
                      ? locale.language === "pt"
                        ? "Nova senha"
                        : "New password"
                      : locale.language === "pt"
                        ? "Senha"
                        : "Password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            )}

            {mode === "reset" && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={locale.language === "pt" ? "Confirmar senha" : "Confirm password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            )}

            {mode === "login" && (
              <div className="text-right">
                <button type="button" onClick={() => setMode("forgot")} className="text-sm text-primary hover:underline">
                  {locale.language === "pt" ? "Esqueceu a senha?" : "Forgot password?"}
                </button>
              </div>
            )}

            <Button type="submit" variant="hero" className="w-full" disabled={isLoading}>
              {isLoading ? (
                locale.language === "pt" ? "Carregando..." : "Loading..."
              ) : (
                <>
                  {mode === "login" && (locale.language === "pt" ? "Entrar" : "Log In")}
                  {mode === "signup" && (locale.language === "pt" ? "Criar Conta" : "Create Account")}
                  {mode === "forgot" && (locale.language === "pt" ? "Enviar Email" : "Send Reset Email")}
                  {mode === "reset" && (locale.language === "pt" ? "Atualizar senha" : "Update password")}
                  <ArrowRight size={18} />
                </>
              )}
            </Button>
          </form>

          {(mode === "login" || mode === "signup") && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === "login" ? (
                <>
                  {locale.language === "pt" ? "Não tem conta?" : "Don't have an account?"}{" "}
                  <button onClick={() => setMode("signup")} className="text-primary hover:underline font-medium">
                    {locale.language === "pt" ? "Cadastre-se" : "Sign up"}
                  </button>
                </>
              ) : (
                <>
                  {locale.language === "pt" ? "Já tem conta?" : "Already have an account?"}{" "}
                  <button onClick={() => setMode("login")} className="text-primary hover:underline font-medium">
                    {locale.language === "pt" ? "Entrar" : "Log in"}
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
